import { defineStore } from 'pinia'
import { ref, unref } from 'vue'
import { CLUSTER } from '../utils/NODE_ENV'
import { useStytchStore } from './stytchStore'

// const BASE_URL = `https://api.user-management-api.orb.local`
const BASE_URL = `https://user-management-api.${CLUSTER}.inhausai-infra.services`
const TOKEN_TYPE = 'Bearer'


type AnyRecord = Record<string, any>

export interface OrganizationSummary {
    id: string
    slug: string
    name: string
    isEnabled?: boolean
    metadata?: AnyRecord
    trusted_metadata?: AnyRecord
    created_at?: string
    raw: OrganizationRecord
}

export interface RoleSummary {
    id: string
    name: string
    description?: string
    permissions: string[]
    raw: AnyRecord
}

export interface MemberSummary {
    user_id: string
    email: string
    name: string
    status: string
    blocked: boolean
    roles: RoleSummary[]
    last_sign_in_at?: string
    invited_at?: string
    created_at?: string
    raw: AnyRecord
}

interface OrganizationRecord extends AnyRecord {
    organization_id: string
    organization_name?: string
    organization_slug?: string
    name?: string
    display_name?: string
    id?: string
}


const slugify = (value: string) =>
    value
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .trim()
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase()

const parseJSONSafely = async (response: Response) => {
    const text = await response.text()
    if (!text) return null
    try {
        return JSON.parse(text)
    } catch {
        return text
    }
}

const deriveTeamId = () => {
    try {
        const cached = localStorage.getItem('selectedTeam')
        if (!cached) return null
        const parsed = JSON.parse(cached)
        return parsed?.id || parsed?.organization_id || null
    } catch {
        return null
    }
}

const normalizeRole = (entry: AnyRecord): RoleSummary => ({
    id: entry?.role_id || entry?.id || '',
    name: entry?.name || entry?.role_id || entry?.id || 'Unnamed Role',
    description: entry?.description || '',
    permissions: Array.isArray(entry?.permissions) ? entry.permissions : [],
    raw: entry || {},
})

const normalizeOrganization = (entry: AnyRecord | null | undefined): OrganizationSummary | null => {
    if (!entry) return null
    const org: OrganizationRecord = (entry.organization as OrganizationRecord) || (entry as OrganizationRecord)
    const id =
        org.organization_id ||
        org.id ||
        org.slug ||
        org.organization_slug ||
        (typeof org.name === 'string' ? slugify(org.name) : '')
    if (!id) return null
    const slug = org.organization_slug || org.slug || slugify(org.name || org.organization_name || id)
    const name =
        org.organization_name ||
        org.display_name ||
        org.name ||
        org.organization_slug ||
        slug ||
        id
    return {
        id,
        slug,
        name,
        isEnabled: typeof org.isEnabled === 'boolean' ? org.isEnabled : undefined,
        metadata: (org as AnyRecord)?.metadata || undefined,
        trusted_metadata: (org as AnyRecord)?.trusted_metadata || undefined,
        created_at: (org as AnyRecord)?.created_at,
        raw: org,
    }
}

const normalizeMember = (entry: AnyRecord): MemberSummary => {
    const member = entry || {}
    const status = String(member.status || member.member_status || 'unknown')
    const roles = Array.isArray(member.roles) ? member.roles.map((role: AnyRecord) => normalizeRole(role)) : []

    return {
        user_id: member.member_id || member.user_id || '',
        email: member.email_address || member.email || '',
        name: member.name || member.full_name || member.email_address || '',
        status,
        blocked: status.toLowerCase() !== 'active',
        roles,
        last_sign_in_at: member.last_sign_in_at,
        invited_at: member.invited_at,
        created_at: member.created_at,
        raw: member,
    }
}

export const useOrganizationStore = defineStore('organization', () => {
    const stytchStore = useStytchStore()
    const organizations = ref<OrganizationSummary[]>([])
    const organization = ref<OrganizationSummary | null>(null)
    const members = ref<MemberSummary[]>([])
    const roles = ref<RoleSummary[]>([])
    const isLoading = ref(false)
    const error = ref<string | null>(null)

    const resetState = () => {
        organizations.value = []
        organization.value = null
        members.value = []
        roles.value = []
        isLoading.value = false
        error.value = null
    }

    const authorizedFetch = async (
        path: string,
        init: RequestInit = {},
        { attachTeamId = false, retry = true }: { attachTeamId?: boolean; retry?: boolean } = {}
    ): Promise<Response> => {
        const buildHeaders = async (forceRefresh = false) => {
            const headers = new Headers(init.headers || {})
            
            const token = stytchStore.stytch.session.getTokens().session_jwt
            console.log('Fetched token:', token, 'forceRefresh:', forceRefresh)
            headers.set('Authorization', `${TOKEN_TYPE} ${token}`)

            if (!headers.has('Accept')) {
                headers.set('Accept', 'application/json')
            }
            if (init.body && !headers.has('Content-Type')) {
                headers.set('Content-Type', 'application/json')
            }
            if (attachTeamId) {
                const teamId = deriveTeamId()
                if (teamId && !headers.has('X-Team-Id')) {
                    headers.set('X-Team-Id', teamId)
                }
            }

            return headers
        }

        let headers = await buildHeaders()
        let response = await fetch(`${BASE_URL}${path}`, { ...init, headers })

        if (response.status === 401 && retry) {
            headers = await buildHeaders(true)
            response = await fetch(`${BASE_URL}${path}`, { ...init, headers })
        }

        return response
    }

    const handleResponse = async <T>(response: Response, fallbackError: string): Promise<T> => {
        if (response.ok) {
            if (response.status === 204) {
                return undefined as unknown as T
            }
            return (await parseJSONSafely(response)) as T
        }

        const payload = await parseJSONSafely(response)
        const reason = typeof payload === 'string' ? payload : payload?.error || payload?.message || fallbackError
        throw new Error(reason || fallbackError)
    }

    const fetchOrganizations = async () => {
        isLoading.value = true
        error.value = null
        try {
            const response = await authorizedFetch('/rest/organizations')
            const data = (await handleResponse<AnyRecord[] | AnyRecord>(response, 'Failed to fetch organizations')) || []
            const list = Array.isArray(data) ? data : data?.organizations || []
            const normalized = list
                .map((item: AnyRecord) => normalizeOrganization(item))
                .filter((item: OrganizationSummary | null): item is OrganizationSummary => Boolean(item))

            organizations.value = normalized
            return { success: true, organizations: normalized }
        } catch (e: any) {
            error.value = e?.message || 'Unable to load organizations'
            organizations.value = []
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    const fetchOrganization = async (organizationId?: string) => {
        isLoading.value = true
        error.value = null
        try {
            let response: Response
            if (organizationId) {
                response = await authorizedFetch(`/rest/organizations/${organizationId}`)
            } else {
                response = await authorizedFetch('/rest/organization')
            }

            const data = await handleResponse<AnyRecord>(response, 'Failed to fetch organization')
            const normalized = normalizeOrganization(data)
            if (normalized) {
                organization.value = normalized
                const index = organizations.value.findIndex((entry) => entry.id === normalized.id)
                if (index === -1) {
                    organizations.value = [...organizations.value, normalized]
                } else {
                    organizations.value.splice(index, 1, normalized)
                }
            }
            return { success: true, organization: organization.value }
        } catch (e: any) {
            error.value = e?.message || 'Unable to fetch organization'
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    const buildOrganizationPayload = (payload: AnyRecord, { ensureSlug }: { ensureSlug?: boolean } = {}) => {
        console.log('Building organization payload from:', payload, 'ensureSlug:', ensureSlug)
        const body: AnyRecord = { ...payload }
        const name = payload.name
        const slug = payload.slug

        if (name) {
            console.log('Setting name in payload:', name)
            body.name = name
        }
        if (slug) {
            body.slug = ensureSlug ? slugify(slug) : slug
        }

        return body
    }

    const createOrganization = async (payload: AnyRecord) => {
        isLoading.value = true
        error.value = null
        try {
            const body = buildOrganizationPayload(payload, { ensureSlug: true })
            const response = await authorizedFetch('/rest/organizations', {
                method: 'POST',
                body: JSON.stringify(body),
            })
            const data = await handleResponse<AnyRecord>(response, 'Failed to create organization')
            const normalized = normalizeOrganization(data)
            if (normalized) {
                organizations.value = [...organizations.value, normalized]
            }
            return { success: true, organization: normalized }
        } catch (e: any) {
            error.value = e?.message || 'Unable to create organization'
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    const editOrganization = async (organizationId: string, payload: AnyRecord) => {
        if (!organizationId) throw new Error('organizationId is required')
        isLoading.value = true
        error.value = null
        try {
            const body = buildOrganizationPayload(payload, { ensureSlug: false })
            const response = await authorizedFetch(`/rest/organizations/${organizationId}`, {
                method: 'PATCH',
                body: JSON.stringify(body),
            })
            const data = await handleResponse<AnyRecord>(response, 'Failed to update organization')
            const normalized = normalizeOrganization(data)
            if (normalized) {
                const index = organizations.value.findIndex((entry) => entry.id === normalized.id)
                if (index === -1) {
                    organizations.value = [...organizations.value, normalized]
                } else {
                    organizations.value.splice(index, 1, normalized)
                }
                if (organization.value?.id === normalized.id) {
                    organization.value = normalized
                }
            }
            return { success: true, organization: normalized }
        } catch (e: any) {
            error.value = e?.message || 'Unable to update organization'
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    const deleteOrganization = async (organizationId: string) => {
        if (!organizationId) throw new Error('organizationId is required')
        isLoading.value = true
        error.value = null
        try {
            const response = await authorizedFetch(`/rest/organizations/${organizationId}`, {
                method: 'DELETE',
            })
            await handleResponse<null>(response, 'Failed to delete organization')
            organizations.value = organizations.value.filter((entry) => entry.id !== organizationId)
            if (organization.value?.id === organizationId) {
                organization.value = null
            }
            return { success: true }
        } catch (e: any) {
            error.value = e?.message || 'Unable to delete organization'
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    const fetchRoles = async () => {
        isLoading.value = true
        error.value = null
        try {
            const response = await authorizedFetch('/rest/roles', {}, { attachTeamId: true })
            const data = await handleResponse<AnyRecord[]>(response, 'Failed to fetch roles')
            const normalized = (Array.isArray(data) ? data : []).map((role) => normalizeRole(role))
            roles.value = normalized
            return normalized
        } catch (e: any) {
            error.value = e?.message || 'Unable to fetch roles'
            roles.value = []
            return []
        } finally {
            isLoading.value = false
        }
    }

    const fetchOrganizationUsers = async () => {
        isLoading.value = true
        error.value = null
        try {
            const teamId = JSON.parse(localStorage.getItem('selectedTeam')).metadata.organization_id
            console.log('Fetching users for teamId:', teamId)
            const response = await authorizedFetch(`/rest/users/orgs/${teamId}`, {}, { attachTeamId: true })
            const data = await handleResponse<AnyRecord[]>(response, 'Failed to fetch organization users')
            const normalized = (Array.isArray(data) ? data : []).map((user) => normalizeMember(user))
            members.value = normalized
            return normalized
        } catch (e: any) {
            error.value = e?.message || 'Unable to fetch organization users'
            members.value = []
            return []
        } finally {
            isLoading.value = false
        }
    }

    const fetchUser = async (memberId: string) => {
        if (!memberId) throw new Error('memberId is required')
        isLoading.value = true
        error.value = null
        try {
            const response = await authorizedFetch(`/rest/users/${memberId}`, {}, { attachTeamId: true })
            const data = await handleResponse<AnyRecord>(response, 'Failed to fetch user')
            const normalized = normalizeMember(data)
            return normalized
        } catch (e: any) {
            error.value = e?.message || 'Unable to fetch user'
            return null
        } finally {
            isLoading.value = false
        }
    }

    const inviteMemberToOrganization = async (email: string, roleId?: string, name?: string) => {
        if (!email) throw new Error('email is required to invite a member')
        isLoading.value = true
        error.value = null
        try {
            const payload: AnyRecord = {
                email,
            }
            if (roleId) {
                payload.roles = [roleId]
            }
            if (name) {
                payload.name = name
            }

            const response = await authorizedFetch('/rest/users/invite', {
                method: 'POST',
                body: JSON.stringify(payload),
            }, { attachTeamId: true })
            const data = await handleResponse<AnyRecord>(response, 'Failed to invite member')
            const normalized = normalizeMember(data)
            members.value = [...members.value, normalized]
            return { success: true, member: normalized }
        } catch (e: any) {
            error.value = e?.message || 'Unable to invite member'
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    const updateOrganizationUser = async (memberId: string, payload: AnyRecord) => {
        if (!memberId) throw new Error('memberId is required to update a user')
        isLoading.value = true
        error.value = null
        try {
            const { roleId, roles: roleList, ...rest } = payload
            const body: AnyRecord = { ...rest }
            if (roleId) {
                body.roles = [roleId]
            } else if (Array.isArray(roleList)) {
                body.roles = roleList
            }

            const response = await authorizedFetch(`/rest/users/${memberId}`, {
                method: 'PUT',
                body: JSON.stringify(body),
            }, { attachTeamId: true })

            const data = await handleResponse<AnyRecord>(response, 'Failed to update member')
            const normalized = normalizeMember(data)
            const index = members.value.findIndex((entry) => entry.user_id === normalized.user_id)
            if (index === -1) {
                members.value = [...members.value, normalized]
            } else {
                members.value.splice(index, 1, normalized)
            }
            return { success: true, member: normalized }
        } catch (e: any) {
            error.value = e?.message || 'Unable to update member'
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    const deleteOrganizationMember = async (memberId: string) => {
        if (!memberId) throw new Error('memberId is required to delete a user')
        isLoading.value = true
        error.value = null
        try {
            const response = await authorizedFetch(`/rest/users/${memberId}`, {
                method: 'DELETE',
            }, { attachTeamId: true })
            await handleResponse<null>(response, 'Failed to delete member')
            members.value = members.value.filter((entry) => entry.user_id !== memberId)
            return { success: true }
        } catch (e: any) {
            error.value = e?.message || 'Unable to delete member'
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    const clear = () => {
        resetState()
    }

    return {
        organizations,
        organization,
        members,
        roles,
        isLoading,
        error,
        fetchOrganizations,
        fetchOrganization,
        createOrganization,
        editOrganization,
        deleteOrganization,
        fetchRoles,
        fetchOrganizationUsers,
        fetchUser,
        inviteMemberToOrganization,
        updateOrganizationUser,
        deleteOrganizationMember,
        clear,
    }
})

